import { DemoProcessing } from "../API";

/**
 * A demo action can be refused with `demo_processing` while Dooglie is still
 * working through the caller's last chat line. That is a "not yet", not a
 * "no": the same call is repeated after the server's `retry_after_s`, for as
 * long as a salesperson will reasonably wait, before it is reported as a
 * failure.
 */

export const PROCESSING_WAIT_LIMIT_MS = 120_000;
export const DEFAULT_RETRY_AFTER_S = 5;
export const STILL_PROCESSING_MESSAGE =
  "Dooglie is still processing the last chat line — try again in a moment";

export const isProcessingRefusal = (e: unknown): e is DemoProcessing =>
  typeof e === "object" && e != null && (e as { error?: unknown }).error === "demo_processing";

export const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

type Options = {
  // Called each time the call is refused and a wait begins, so the panel can
  // say it is finishing rather than stuck.
  onWait: () => void;
  // The wait itself; tests pass their own.
  wait?: (ms: number) => Promise<void>;
};

/**
 * Runs `call` until it answers with something other than `demo_processing`,
 * waiting `retry_after_s` between attempts. Any other refusal is rethrown as
 * is. Once the waits would pass two minutes it gives up with
 * `STILL_PROCESSING_MESSAGE`, in the `{error, message}` shape every other
 * refusal has, so the panel shows it the same way.
 */
export async function untilProcessed<T>(
  call: () => Promise<T>,
  { onWait, wait = sleep }: Options,
): Promise<T> {
  let waited = 0;
  for (;;) {
    try {
      return await call();
    } catch (e) {
      if (!isProcessingRefusal(e)) throw e;
      const ms = (e.retry_after_s ?? DEFAULT_RETRY_AFTER_S) * 1000;
      if (waited + ms > PROCESSING_WAIT_LIMIT_MS) {
        throw { error: "demo_processing", message: STILL_PROCESSING_MESSAGE };
      }
      onWait();
      await wait(ms);
      waited += ms;
    }
  }
}
